import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import { DocumentArrowUpIcon, SparklesIcon, ArrowLeftIcon } from '../../lib/commonUtils';

import useAppStore from '../../store/appStore';
import { todayDateLocal, nowHHMMLocal } from '../../lib/commonUtils';

const BatchCallForm = () => {
  const navigate = useNavigate();

  const { createBatchCallingJob, batchCalling, fetchDashboardAnalysis } = useAppStore((s) => ({
    createBatchCallingJob: s.createBatchCallingJob,
    batchCalling: s.batchCalling,
    fetchDashboardAnalysis: s.fetchDashboardAnalysis,
  }));

  const [uaLoading, setUaLoading] = useState(true);
  const [uaError, setUaError] = useState('');
  const [uaAgentsRaw, setUaAgentsRaw] = useState([]);

  const fetchUserAgents = useCallback(async () => {
    setUaLoading(true);
    setUaError('');
    try {
      // Use cached store data — no extra network request
      const result = await fetchDashboardAnalysis();
      const rows = result?.data?.your_ai_agents?.test_speed || [];
      setUaAgentsRaw(rows);
    } catch (e) {
      setUaError(e?.response?.data?.message || e?.message || 'Failed to load agents');
      setUaAgentsRaw([]);
    } finally {
      setUaLoading(false);
    }
  }, [fetchDashboardAnalysis]);

  useEffect(() => {
    fetchUserAgents();
  }, [fetchUserAgents]);

  const normalizedAgents = useMemo(() => {
    const list = Array.isArray(uaAgentsRaw) ? uaAgentsRaw : [];
    return list.map((a, i) => {
      const name = a?.agent_name || a?.name || `Agent ${i + 1}`;
      return { id: name, name };
    });
  }, [uaAgentsRaw]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      phone_column: '',
      agent_name: '',
      call_name: '',
      when_mode: 'now',
      scheduled_date: todayDateLocal(),
      scheduled_time: nowHHMMLocal(),
    },
  });

  const whenMode = watch('when_mode');

  useEffect(() => {
    if (!uaLoading && normalizedAgents.length === 1) {
      setValue('agent_name', normalizedAgents[0].name);
    }
  }, [uaLoading, normalizedAgents, setValue]);

  const fileInputRef = useRef(null);
  const [csvFile, setCsvFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const nameOk = /\.csv$/i.test(file.name);
    const typeOk = (file.type || '').toLowerCase() === 'text/csv' || nameOk;
    if (!nameOk || !typeOk) {
      toast.error('Please upload a valid CSV (.csv) file');
      return;
    }
    const cloned = new File([file.slice(0, file.size, file.type)], file.name, {
      type: file.type || 'text/csv',
      lastModified: Date.now(),
    });
    setCsvFile(cloned);
  };

  const onSubmit = async (data) => {
    if (!csvFile) {
      toast.error('Please upload a CSV file');
      return;
    }

    let scheduled_time_unix = undefined;

    if (data.when_mode === 'later') {
      const dateVal = String(data.scheduled_date || '').trim();
      const timeVal = String(data.scheduled_time || '').trim();

      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
        toast.error('Please choose a valid date');
        return;
      }
      if (!/^\d{2}:\d{2}$/.test(timeVal)) {
        toast.error('Please choose a valid time');
        return;
      }

      const [y, m, d] = dateVal.split('-').map(Number);
      const [hh, mm] = timeVal.split(':').map(Number);
      const instant = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
      scheduled_time_unix = Math.floor(instant.getTime() / 1000);
    }

    try {
      setIsUploading(true);
      const res = await createBatchCallingJob({
        agent_name: data.agent_name,
        call_name: data.call_name,
        phone_column: data.phone_column || 'phone',
        scheduled_time_unix,
        csvOrExcelFile: csvFile,
      });

      if (res?.success) {
        toast.success('Batch calling job submitted successfully!');
        navigate('/batchcallinglist');
      } else {
        toast.error(res?.error || 'Batch call submission failed');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const loading = !!batchCalling?.creating;
  const agentsLoading = uaLoading;

  return (
    <div className="space-y-12 relative pb-20">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-center">
          <Link
            to="/batchcallinglist"
            className="mr-6 p-3 bg-white/5 border border-white/10 rounded-2xl text-white/40 hover:text-white hover:bg-white/10 transition-all group"
          >
            <ArrowLeftIcon className="h-6 w-6 group-hover:-translate-x-1 transition-transform" />
          </Link>
          <div>
            <h1 className="text-2xl sm:text-2xl font-bold text-white tracking-tight">Initiate Batch Call</h1>
            <p className="mt-1 text-sm sm:text-xs font-medium text-white/30 italic">
              Scale your communication with automated voice campaigns
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-10 sm:space-y-6">
        <div className="controlled-glass p-10 sm:p-6 rounded-[40px] sm:rounded-2xl inner-highlight">
          <div className="flex items-center space-x-4 mb-8 sm:mb-5">
            <div className="p-2.5 sm:p-1.5 bg-white/5 border border-white/10 rounded-xl sm:rounded-lg">
              <SparklesIcon className="h-5 w-5 sm:h-4 w-4 text-primary-400" />
            </div>
            <h3 className="text-xl sm:text-lg font-bold text-white tracking-tight">1. Campaign Configuration</h3>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:gap-5 sm:grid-cols-2">
            <div>
              <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">
                Select Agent <span className="text-red-400 font-bold ml-1">*</span>
              </label>
              <select
                disabled={agentsLoading}
                {...register('agent_name', { required: 'Agent name is required' })}
                className={`input text-base sm:text-sm py-4 sm:py-2.5 appearance-none ${errors.agent_name ? 'border-red-500/50' : ''}`}
              >
                <option value="" className="bg-[#0a0f1d]">{agentsLoading ? 'Loading Agents...' : 'Choose an Agent'}</option>
                {normalizedAgents.map((agent) => (
                  <option key={agent.id} value={agent.name} className="bg-[#0a0f1d]">
                    {agent.name}
                  </option>
                ))}
              </select>
              {uaError && <p className="mt-2 text-xs font-bold text-red-400 italic">{uaError}</p>}
              {errors.agent_name && (
                <p className="mt-2 text-xs font-bold text-red-400 italic">{errors.agent_name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">
                Batch Name <span className="text-red-400 font-bold ml-1">*</span>
              </label>
              <input
                type="text"
                className={`input text-base sm:text-sm py-4 sm:py-2.5 ${errors.call_name ? 'border-red-500/50' : ''}`}
                placeholder="Ex: Customer Feedback Q1"
                {...register('call_name', { required: 'Call name is required' })}
              />
              {errors.call_name && (
                <p className="mt-2 text-xs font-bold text-red-400 italic">{errors.call_name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">Activation Timeline</label>
              <select
                {...register('when_mode')}
                className="input text-base sm:text-sm py-4 sm:py-2.5 appearance-none"
              >
                <option value="now" className="bg-[#0a0f1d]">Execute Immediately</option>
                <option value="later" className="bg-[#0a0f1d]">Schedule for Future</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">CSV Phone Column</label>
              <input
                type="text"
                className={`input text-base sm:text-sm py-4 sm:py-2.5 ${errors.phone_column ? 'border-red-500/50' : ''}`}
                placeholder="Ex: phone_number"
                {...register('phone_column')}
              />
              {errors.phone_column && (
                <p className="mt-2 text-xs font-bold text-red-400 italic">{errors.phone_column.message}</p>
              )}
            </div>

            {whenMode === 'later' && (
              <>
                <div className="animate-in fade-in slide-in-from-top-4">
                  <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">
                    Scheduled Date <span className="text-red-400 font-bold ml-1">*</span>
                  </label>
                  <input
                    type="date"
                    className={`input text-base sm:text-sm py-4 sm:py-2.5 ${errors.scheduled_date ? 'border-red-500/50' : ''}`}
                    {...register('scheduled_date', {
                      validate: (v) =>
                        whenMode === 'now' ||
                        /^\d{4}-\d{2}-\d{2}$/.test(String(v || '')) ||
                        'Date is required',
                    })}
                  />
                  {errors.scheduled_date && (
                    <p className="mt-2 text-xs font-bold text-red-400 italic">{errors.scheduled_date.message}</p>
                  )}
                </div>
                <div className="animate-in fade-in slide-in-from-top-4">
                  <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">
                    Scheduled Time <span className="text-red-400 font-bold ml-1">*</span>
                  </label>
                  <input
                    type="time"
                    step="60"
                    className={`input text-base sm:text-sm py-4 sm:py-2.5 ${errors.scheduled_time ? 'border-red-500/50' : ''}`}
                    {...register('scheduled_time', {
                      validate: (v) =>
                        whenMode === 'now' ||
                        /^\d{2}:\d{2}$/.test(String(v || '')) ||
                        'Time is required',
                    })}
                  />
                  {errors.scheduled_time && (
                    <p className="mt-2 text-xs font-bold text-red-400 italic">{errors.scheduled_time.message}</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="controlled-glass p-10 sm:p-6 rounded-[40px] sm:rounded-2xl inner-highlight">
          <div className="flex items-center space-x-4 mb-4 sm:mb-3">
            <div className="p-2.5 sm:p-1.5 bg-white/5 border border-white/10 rounded-xl sm:rounded-lg">
              <DocumentArrowUpIcon className="h-5 w-5 sm:h-4 w-4 text-amber-400" />
            </div>
            <h3 className="text-xl sm:text-lg font-bold text-white tracking-tight">2. Recipient Data</h3>
          </div>
          <p className="text-sm sm:text-xs font-medium text-white/30 italic mb-8 sm:mb-5 ml-14 sm:ml-12">
            Provide a CSV file with destination numbers and custom fields.
          </p>

          <div
            className="relative group cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="absolute inset-0 bg-white/[0.02] border-2 border-dashed border-white/10 rounded-[32px] sm:rounded-xl group-hover:bg-white/[0.05] group-hover:border-amber-500/30 transition-all" />
            <div className="relative py-14 sm:py-10 px-6 text-center space-y-4 sm:space-y-2">
              <div className="p-5 sm:p-3 bg-white/5 rounded-2xl sm:rounded-xl w-fit mx-auto border border-white/10 group-hover:bg-amber-500/10 group-hover:border-amber-500/20 transition-all">
                <DocumentArrowUpIcon className="h-10 w-10 sm:h-7 w-7 text-white/20 group-hover:text-amber-400" />
              </div>
              <div>
                <p className="text-base sm:text-sm font-bold text-white">Select Recipient File</p>
                <p className="text-[10px] sm:text-[9px] font-bold text-white/20 uppercase tracking-widest mt-1">Upload .csv only (max 10,000 rows)</p>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="sr-only"
              onChange={handleFileChange}
            />
          </div>

          {csvFile && (
            <div className="mt-6 flex items-center justify-between p-5 sm:p-3.5 bg-amber-500/5 border border-amber-500/20 rounded-[24px] sm:rounded-xl animate-in fade-in slide-in-from-top-2">
              <div className="flex items-center space-x-4 sm:space-x-3">
                <div className="p-3 sm:p-2 bg-amber-500/20 rounded-xl sm:rounded-lg">
                  <DocumentArrowUpIcon className="h-5 w-5 sm:h-4 w-4 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm sm:text-xs font-bold text-white">{csvFile.name}</p>
                  <p className="text-[10px] sm:text-[8px] font-bold text-white/40 uppercase tracking-widest">Ready for deployment</p>
                </div>
              </div>
              <button
                type="button"
                className="text-[10px] sm:text-[9px] font-bold uppercase tracking-widest text-red-500 hover:text-red-400 p-3 sm:p-2"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isUploading) return;
                  setCsvFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              >
                Remove
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end items-center gap-6 sm:gap-4 pt-10 sm:pt-6">
          <Link
            to="/batchcallinglist"
            className="btn btn-secondary w-full sm:w-auto sm:px-8"
          >
            Discard Campaign
          </Link>
          <button
            type="submit"
            disabled={isUploading || batchCalling?.creating}
            className="btn btn-primary w-full sm:w-auto sm:px-12"
          >
            <span className="flex items-center justify-center">
              {isUploading || batchCalling?.creating ? (
                <>
                  <div className="h-4 w-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-3" />
                  Deploying...
                </>
              ) : (
                <>
                  <SparklesIcon className="h-4 w-4" />
                  Launch Campaign
                </>
              )}
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default BatchCallForm;